import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import actualIcon from '../../../assets/windowsIcons/imageViewer/actual.png';
import deleteIcon from '../../../assets/windowsIcons/imageViewer/delete.png';
import editIcon from '../../../assets/windowsIcons/imageViewer/edit.png';
import fitIcon from '../../../assets/windowsIcons/imageViewer/fit.png';
import helpIcon from '../../../assets/windowsIcons/imageViewer/help.png';
import nextIcon from '../../../assets/windowsIcons/imageViewer/next.png';
import previousIcon from '../../../assets/windowsIcons/imageViewer/previous.png';
import printIcon from '../../../assets/windowsIcons/imageViewer/print.png';
import rotateLeftIcon from '../../../assets/windowsIcons/imageViewer/rotate-left.png';
import rotateRightIcon from '../../../assets/windowsIcons/imageViewer/rotate-right.png';
import saveIcon from '../../../assets/windowsIcons/imageViewer/save.png';
import slideshowIcon from '../../../assets/windowsIcons/imageViewer/slideshow.png';
import zoomInIcon from '../../../assets/windowsIcons/imageViewer/zoom-in.png';
import zoomOutIcon from '../../../assets/windowsIcons/imageViewer/zoom-out.png';

const ZOOM_LEVELS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4];

function ImageViewer({ images = [], initialIndex = 0 }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState('fit');
  const [rotation, setRotation] = useState(0);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [isSlideshow, setIsSlideshow] = useState(false);

  function showImage(index) {
    setCurrentIndex(index);
    setZoom('fit');
    setRotation(0);
    setIsSlideshow(false);
  }

  function changeZoom(direction) {
    const current = zoom === 'fit' ? 1 : zoom;
    const next =
      direction > 0
        ? ZOOM_LEVELS.find(level => level > current)
        : [...ZOOM_LEVELS].reverse().find(level => level < current);
    if (next) setZoom(next);
  }

  useEffect(() => {
    if (!isSlideshow || images.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setCurrentIndex(index => (index + 1) % images.length);
      setZoom('fit');
      setRotation(0);
    }, 2500);
    return () => window.clearInterval(timer);
  }, [images.length, isSlideshow]);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'ArrowRight') {
        setCurrentIndex(prev => {
          const next = Math.min(images.length - 1, prev + 1);
          if (next !== prev) {
            setZoom('fit');
            setRotation(0);
          }
          return next;
        });
      } else if (e.key === 'ArrowLeft') {
        setCurrentIndex(prev => {
          const next = Math.max(0, prev - 1);
          if (next !== prev) {
            setZoom('fit');
            setRotation(0);
          }
          return next;
        });
      } else if (e.key === '+' || e.key === '=') {
        changeZoom(1);
      } else if (e.key === '-') {
        changeZoom(-1);
      } else if (e.key === '0') {
        setZoom(1);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [images.length, zoom]);

  if (!images || images.length === 0) return null;

  const currentImage = images[currentIndex];
  const imageName = currentImage ? currentImage.split('/').pop() : '';
  const imageStyle =
    zoom === 'fit'
      ? { maxWidth: '100%', maxHeight: '100%' }
      : {
          width: naturalSize.width ? `${naturalSize.width * zoom}px` : 'auto',
          height: naturalSize.height
            ? `${naturalSize.height * zoom}px`
            : 'auto',
          maxWidth: 'none',
          maxHeight: 'none',
        };

  return (
    <ViewerContainer>
      <div className="viewer-content">
        <div className="viewer-stage">
          <img
            src={currentImage}
            alt={imageName}
            className="viewer-img"
            style={{ ...imageStyle, transform: `rotate(${rotation}deg)` }}
            onLoad={event =>
              setNaturalSize({
                width: event.currentTarget.naturalWidth,
                height: event.currentTarget.naturalHeight,
              })
            }
          />
        </div>
      </div>
      <div className="viewer-toolbar" role="toolbar" aria-label="Picture tools">
        <button
          className="viewer-btn viewer-nav"
          title="Previous Image (Left Arrow)"
          aria-label="Previous image"
          onClick={() => showImage(Math.max(0, currentIndex - 1))}
          disabled={currentIndex === 0}
        >
          <img className="viewer-icon" src={previousIcon} alt="" aria-hidden="true" />
        </button>
        <button
          className="viewer-btn viewer-nav"
          title="Next Image (Right Arrow)"
          aria-label="Next image"
          onClick={() => showImage(Math.min(images.length - 1, currentIndex + 1))}
          disabled={currentIndex === images.length - 1}
        >
          <img className="viewer-icon" src={nextIcon} alt="" aria-hidden="true" />
        </button>
        <span className="viewer-separator" />
        <button
          className={`viewer-btn ${zoom === 'fit' ? 'active' : ''}`}
          title="Best Fit"
          aria-label="Fit image to window"
          onClick={() => setZoom('fit')}
        >
          <img className="viewer-icon" src={fitIcon} alt="" aria-hidden="true" />
        </button>
        <button
          className={`viewer-btn ${zoom === 1 ? 'active' : ''}`}
          title="Actual Size"
          aria-label="Show actual size"
          onClick={() => setZoom(1)}
        >
          <img className="viewer-icon" src={actualIcon} alt="" aria-hidden="true" />
        </button>
        <button
          className={`viewer-btn ${isSlideshow ? 'active' : ''}`}
          title="Start Slide Show"
          aria-label="Start slide show"
          onClick={() => setIsSlideshow(value => !value)}
          disabled={images.length < 2}
        >
          <img className="viewer-icon" src={slideshowIcon} alt="" aria-hidden="true" />
        </button>
        <span className="viewer-separator" />
        <button
          className="viewer-btn"
          title="Zoom In (+)"
          aria-label="Zoom in"
          onClick={() => changeZoom(1)}
          disabled={zoom === ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
        >
          <img className="viewer-icon" src={zoomInIcon} alt="" aria-hidden="true" />
        </button>
        <button
          className="viewer-btn"
          title="Zoom Out (-)"
          aria-label="Zoom out"
          onClick={() => changeZoom(-1)}
          disabled={zoom === ZOOM_LEVELS[0]}
        >
          <img className="viewer-icon" src={zoomOutIcon} alt="" aria-hidden="true" />
        </button>
        <span className="viewer-separator" />
        <button
          className="viewer-btn"
          title="Rotate Clockwise"
          aria-label="Rotate clockwise"
          onClick={() => setRotation(value => value + 90)}
        >
          <img className="viewer-icon" src={rotateRightIcon} alt="" aria-hidden="true" />
        </button>
        <button
          className="viewer-btn"
          title="Rotate Counterclockwise"
          aria-label="Rotate counterclockwise"
          onClick={() => setRotation(value => value - 90)}
        >
          <img className="viewer-icon" src={rotateLeftIcon} alt="" aria-hidden="true" />
        </button>
        <span className="viewer-separator" />
        <button className="viewer-btn" title="Delete" aria-label="Delete" disabled>
          <img className="viewer-icon" src={deleteIcon} alt="" aria-hidden="true" />
        </button>
        <button className="viewer-btn" title="Print" aria-label="Print" disabled>
          <img className="viewer-icon" src={printIcon} alt="" aria-hidden="true" />
        </button>
        <button className="viewer-btn" title="Save As" aria-label="Save as" disabled>
          <img className="viewer-icon" src={saveIcon} alt="" aria-hidden="true" />
        </button>
        <button className="viewer-btn" title="Edit Image" aria-label="Edit image" disabled>
          <img className="viewer-icon" src={editIcon} alt="" aria-hidden="true" />
        </button>
        <span className="viewer-separator" />
        <button className="viewer-btn" title="Help" aria-label="Help" disabled>
          <img className="viewer-icon" src={helpIcon} alt="" aria-hidden="true" />
        </button>
      </div>
    </ViewerContainer>
  );
}

const ViewerContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-height: 0;
  background: #ece9d8;

  .viewer-content {
    min-height: 0;
    flex: 1;
    margin: 6px 6px 0;
    overflow: auto;
    background: #fff;
    border: 1px solid #000;
  }
  .viewer-stage {
    min-width: 100%;
    min-height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: visible;
    background: #fff;
  }
  .viewer-img {
    display: block;
    flex: none;
    object-fit: contain;
    transform-origin: center;
  }
  .viewer-toolbar {
    box-sizing: border-box;
    min-height: 38px;
    padding: 3px 5px 4px;
    background: #ece9d8;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0;
    overflow-x: auto;
  }
  .viewer-btn {
    box-sizing: border-box;
    width: 25px;
    height: 27px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ece9d8;
    border: 1px solid transparent;
    padding: 2px;
    cursor: pointer;
    &:hover:not(:disabled) {
      border-color: #fff #aca899 #aca899 #fff;
    }
    &:active:not(:disabled),
    &.active {
      border-color: #aca899 #fff #fff #aca899;
      background: #e2e0d2;
    }
    &:disabled {
      opacity: 0.42;
      cursor: default;
    }
  }
  .viewer-separator {
    width: 2px;
    height: 24px;
    margin: 0 4px;
    border-left: 1px solid #aca899;
    border-right: 1px solid #fff;
    flex: none;
  }
  .viewer-icon {
    display: block;
    width: 18px;
    height: 18px;
    object-fit: contain;
    image-rendering: auto;
  }
`;

export default ImageViewer;
